import { DatePicker, DialogActionButton, TextField, TimePicker } from '@finpay-development/shared-components';
import { Checkbox, Grid, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Form, Formik } from 'formik';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { createClientComment } from '../../implementation-specialist/state/clients/implementation-clients-thunk';
import { Comment, emptyComment } from '../../patient/components/models/comment';
import { clearStatus } from '../../patient/state/patient-slice';
import { createPatientNote, getInstanceOfCare } from '../../patient/state/patient-thunk';
import { showErrorStatus } from '../../security/state/user-slice';
import { commentsPage, createCommentSuccesMessages } from '../enums';
import { RootState } from '../state/root-reducer';
import { AppDispatch } from '../state/store';
import { patientService } from '../../patient/services/patient-service';
import { PatientEncounter } from '../../patient/components/models/patient-encounter';
import { createPatientIoc } from '../../admissions-advisor/state/vob-patient-thunk';
import { IocConfig } from '../../admissions-advisor/models/patient';
import { TimingRisk, PayorRisk } from '../../shared/model/timing-and-payor-risk';
import { loadSavedEstimatesListv3, saveSelectedEstimate, saveSelectedEstimateSelfPay } from '../state/estimator-thunk';
import { admissionsAdvisorUtils } from '../../admissions-advisor/utils/admission-advisor-utils';
interface CommentModalProps {
  page: string;
  open: boolean;
  handleCommentModalCancel: () => void;
  handleCommentModalSubmit: () => void;
}

export function CommentModal(props: CommentModalProps) {
  const { page ,open,  handleCommentModalCancel, handleCommentModalSubmit } = props;

  const [showReminder, setShowReminder] = useState(false)
  const [isClientPage, setIsClientPage] = useState(false)

  const [enableSaveButton, setEnableSaveButton] = useState(false);
  const [isChecked, setIsChecked] = useState(false)
  const isLoadingIOCRef = useRef(false);
  const lastLoadedPatientIdRef = useRef<number | undefined>(undefined);

  const dispatch = useDispatch<AppDispatch>();
  const formRef: any = useRef();

  const saveStatus = useSelector(
    (state: RootState) =>
      state.adminContext.adminConfigurationContext.modalSaveStatus
  );

  const selectedEncounter = useSelector(
    (state: RootState) =>
      state.patientContext.selectedEncounter
  );

  const selectedPatient = useSelector(
    (state: RootState) =>
      state.patientContext.selectedPatient
  );

  const selectedVob = useSelector(
    (state: RootState) =>
      state.vobContext.selectedVob
  );

  const selectedEstimate = useSelector(
    (state: RootState) =>
      state.estimatorContext.selectedEstimate
  );

  const allClientsMap = useSelector(
    (state: RootState) =>
      state.implementationContext.implementationSpecialistClient.allClientsMap
  );

  const savedEstimatesList = useSelector(
    (state: RootState) =>
      state.estimatorContext.savedEstimatesList
  );

  const vob = useSelector(
    (state: RootState) =>
      state.vobContext.selectedVob
  );

  const rateAsPayorPlan = useSelector(
    (state: RootState) =>
      state.estimatorContext.rateAsPayorPlan
  );

  useEffect(() => {
    switch (page) {
      case commentsPage.patients:
        setShowReminder(true)
      break;
      case commentsPage.clients:
        setIsClientPage(true)
      break;
      default:
        break;
    }
  }, [page])


  const updateAllEstimatesWithoutIOC = async (patientEncounterId: number, patientId: number, clientId: number, facilityId: number) => {
    if (!savedEstimatesList || !Array.isArray(savedEstimatesList)) {
      return;
    }


    const estimatesWithoutIOC = savedEstimatesList.filter((est: any) => {
      const estimateBody = est.estimateBody || est;
      const estPatientId = estimateBody.patientId || est.patientId;
      return !estimateBody.patientEncounterId && est.pfrEstimateId && estPatientId === patientId;
    });

    if (estimatesWithoutIOC.length === 0) {
      return;
    }


    for (const est of estimatesWithoutIOC) {
      try {
        const estAny = est as any;
        const estimateBody = estAny.estimateBody || est;
        const estimateId = estAny.pfrEstimateId || estAny.estimateId;
        
        if (!estimateId) {
          continue;
        }


        const updateBody = admissionsAdvisorUtils.mapToNewEstimate(
          vob?.vobId || 0,
          clientId,
          facilityId,
          estimateBody,
          estimateBody.advisorPatientId || patientId || 0,
          patientEncounterId.toString()
        );

        updateBody.patientId = patientId;
        if (rateAsPayorPlan) {
          updateBody.rateAsPayorPlan = rateAsPayorPlan;
        }

        const isSelfPayOrPrivatePay = estimateBody.isSelfPay || estimateBody.isPrivatePay;
        
        await dispatch(
          isSelfPayOrPrivatePay
            ? saveSelectedEstimateSelfPay({
                estimateId: estimateId,
                estimate: updateBody,
              })
            : saveSelectedEstimate({
                estimateId: estimateId,
                estimate: updateBody,
              })
        );
      } catch (error) {
        const estAny = est as any;
        console.error(`Error updating estimate ${estAny.pfrEstimateId || estAny.estimateId}:`, error);
      }
    }

    await dispatch(loadSavedEstimatesListv3({
      paramId: -2,
      patientId: patientId,
    }));
  };


  const createIOCIfNeeded = async (): Promise<number | null> => {
    if (page !== commentsPage.patients) {
      return selectedEncounter?.patientEncounterId || null;
    }

    if (selectedEncounter?.patientEncounterId) {
      return selectedEncounter.patientEncounterId;
    }

    const patientId = selectedPatient?.patientId || selectedEncounter?.patientId;
    
    if (!patientId) {
      return null;
    }

    try {
      const response = await patientService.getPatientInstancesOfCare({
        patientId: patientId,
        limit: 100,
        offset: 0,
      });

      if (response.hasErrors) {
        console.error('Error fetching patient encounters:', response.errorMessage);
        return null;
      }

      const encounters = response.entity as PatientEncounter[];

      const notManagedEncounters = encounters.filter((encounter) => {
        const workflowStatusDesc = encounter.workflow?.workflowStatus?.workflowStatusDesc;
        const workflowStatusId = encounter.workflow?.workflowStatus?.workflowStatusId;
        return workflowStatusDesc === 'Not Managed' || workflowStatusId === 20;
      });

      if (notManagedEncounters.length > 0) {
        return notManagedEncounters[0].patientEncounterId;
      }

      const referenceEncounter = encounters.length > 0 ? encounters[0] : null;
      const clientId = referenceEncounter?.clientId 
        || (selectedEstimate?.clientId ? Number(selectedEstimate.clientId) : undefined)
        || selectedVob?.client?.clientId
        || (selectedPatient?.clientId ? Number(selectedPatient.clientId) : 0);

      const targetClient = clientId ? allClientsMap?.get(Number(clientId)) : null;
      const defaultFacilityId = targetClient?.clientFacilities?.[0]?.facilityId;
      const facilityId = referenceEncounter?.facilityId 
        || (selectedEstimate?.facilityId ? Number(selectedEstimate.facilityId) : undefined)
        || selectedVob?.facility?.facilityId
        || defaultFacilityId;

      if (!clientId || !facilityId) {
        console.warn('Cannot create not managed IOC: missing clientId or facilityId', { clientId, facilityId });
        return null;
      }

      const defaultTimingRisk: TimingRisk = {
        timingRiskId: 1,
        timingRiskName: 'Precare',
      };
      const defaultPayorRisk: PayorRisk = {
        payorRiskId: 1,
        payorRiskName: '',
      };

      const iocConfig: IocConfig = {
        paramId: 0,
        patientId: patientId,
        clientId: clientId,
        facilityId: facilityId,
        pfrAmt: 0,
        timingRisk: defaultTimingRisk,
        payorRisk: defaultPayorRisk,
        workflow: {
          workflowId: 2,
          workflowStatus: {
            workflowStatusId: 20,
          },
          workflowSubStatus: {
            workflowSubStatusId: 7,
          },
        },
        finClearanceStatus: 'New',
      };

      const newIOCResponse = await dispatch(createPatientIoc(iocConfig));
      if (newIOCResponse?.payload?.patientEncounterId) {
        await dispatch(getInstanceOfCare({
          patientId: patientId,
          encounterId: newIOCResponse.payload.patientEncounterId,
        }));

        await updateAllEstimatesWithoutIOC(newIOCResponse.payload.patientEncounterId, patientId, clientId, facilityId);
        
        return newIOCResponse.payload.patientEncounterId;
      }
    } catch (error) {
      console.error('Error creating not managed IOC:', error);
    }

    return null;
  };

  const seletedClient = useSelector(
    (state: RootState) =>
      state.implementationContext.implementationSpecialistClient.client
  );

  const errorMessage = useSelector(
    (state: RootState) =>
      state.adminContext.adminConfigurationContext.errorMessage
  );


  async function handleSave() {
    const currentLocalTime = new Date().toISOString()
    const formValues = formRef?.current?.values || {};

    if (isChecked && formValues.completionDueDt && formValues.completionDueTime) {
      const date = new Date(formValues?.completionDueDt);
      const time = new Date(formValues?.completionDueTime);
      const dateWithLocalTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes(), time.getSeconds());
      const dateTimeString = dateWithLocalTime.toISOString();
      formValues.completionDueDt = dateTimeString;
    } else {
      formValues.completionDueDt = ""
    }
    formValues.isReminder = isChecked;
    formValues.noteDt = currentLocalTime;

    if (isClientPage) {
      const clientComment: Comment = emptyComment;
      clientComment.clientId = seletedClient.clientId;
      clientComment.commentText = formValues.noteText;
      clientComment.commentDt = formValues.noteDt;

      dispatch(createClientComment({ message: createCommentSuccesMessages.comment, comment: clientComment }));
    } else {
      let patientEncounterId = selectedEncounter?.patientEncounterId;
      
      if (!patientEncounterId) {
        const createdIOCId = await createIOCIfNeeded();
        if (createdIOCId) {
          patientEncounterId = createdIOCId;
        }
      }

      if (patientEncounterId) {
        formValues.patientEncounterId = patientEncounterId;
      }
      
      if (!formValues.patientId && (selectedPatient?.patientId || selectedEncounter?.patientId)) {
        formValues.patientId = selectedPatient?.patientId || selectedEncounter?.patientId;
      }

      if (patientEncounterId || !formValues.patientEncounterId) {
        dispatch(createPatientNote({ message: createCommentSuccesMessages.comment, comment: formValues }));
      } else {
        dispatch(showErrorStatus('Cannot save comment: IOC is required but could not be created.'));
        return;
      }
    }

    handleCommentModalSubmit();

  }


  const handleCheckBox = () =>{
    setIsChecked(!isChecked)
  }

  function handleSaveCallback(saveSuccessful: boolean) {
    if (saveSuccessful) {
      handleCommentModalSubmit();
      setEnableSaveButton(false);

    } else {
      dispatch(showErrorStatus(errorMessage));
    }
    dispatch(clearStatus());
  }

  function handleCancelCallback() {
    handleCommentModalCancel();
  }

  const initialValues = {
    patientId: selectedPatient?.patientId || selectedEncounter?.patientId,
    patientEncounterId: selectedEncounter?.patientEncounterId,
    clientId: seletedClient?.clientId,
    noteText:"",
    completionDueDt: new Date(),
    completionDueTime: new Date(),
    noteDt:"",
    isReminder: false
  };

  const getValidationSchema = (isChecked?: boolean) => (
    Yup.object(
      !isChecked ? {
        noteText: Yup.string().required("Required"),
      } : {
        noteText: Yup.string().required("Required"),
        completionDueTime: Yup.string().min(1),
        completionDueDt: Yup.string().min(1),
      }
    )
  );

  let validationSchema = getValidationSchema();

  function checkIfIsValid(value: {}) {
    validationSchema
      .validate(value)
      .then(() => {
        setEnableSaveButton(true);
      })
      .catch((err) => {
        setEnableSaveButton(false);
      });
  }

  return (
    <Dialog
      className="modal user-modal"
      open={open}
      fullWidth={true}
      maxWidth="md"
      scroll="body"
    >
      <DialogTitle>Comment</DialogTitle>
      <DialogContent>
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          validate={checkIfIsValid}
          onSubmit={() => {}}
        >
          {(formik) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12} className="mt-4">
                  <TextField

                    error={
                      formik.touched["noteText"] && formik.errors["noteText"]
                        ? formik.errors["noteText"]
                        : ""
                    }
                    label="Comment"
                    name="noteText"
                    multiline
                    value={formik.values.noteText}
                    onChange={(e: Event) => {
                      formik.handleChange(e);
                      checkIfIsValid(formik.values);
                    }}
                    onBlur={formik.handleBlur}
                    helperText="A time stamp and your name will be added automatically"
                    test-id="comment-modal-note-text"
                  />
                </Grid>
                {showReminder && (
                  <Fragment>
                    <Grid xs={4} item className="mt-4">
                      <Checkbox className=""
                        checked={isChecked}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleCheckBox();
                          validationSchema = getValidationSchema(!isChecked);
                          checkIfIsValid(formik.values);
                        }}
                      />
                      <Typography display="inline" >Add a reminder</Typography>
                    </Grid>
                    <Grid xs={4} item className="mt-4">
                      <DatePicker
                        label="Remind On Date"
                        value={formik.values.completionDueDt}
                        onChange={(date: Date) => {
                          formik.setFieldValue('completionDueDt', date)
                        }}
                        disabled={!isChecked}
                      />
                    </Grid>
                    <Grid xs={4} item className="mt-4">
                      <TimePicker
                        label="Remind On Time"
                        value={formik.values.completionDueTime}
                        onChange={(time: Date) => {
                          formik.setFieldValue('completionDueTime', time)
                        }}
                        disabled={!isChecked}
                      />
                    </Grid>
                  </Fragment>
                )}
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions>
        <DialogActionButton
          isEnabled={enableSaveButton}
          savebuttonText= "Save"
          saveStatus={saveStatus}
          executeSave={handleSave}
          handleCallbackSave={handleSaveCallback}
          handleCallbackCancel={handleCancelCallback}
        />
      </DialogActions>
    </Dialog>
  );
}
